import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Home, ChevronRight, ChevronLeft, X, Download, Maximize2, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { projectsData } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const { t, language } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [galleryType, setGalleryType] = useState('exterior');
  const [selectedUnitType, setSelectedUnitType] = useState(null);
  const [activeFloorTab, setActiveFloorTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    unitType: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const project = projectsData.find((p) => p.id === projectId);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset state and scroll to top when project changes
  useEffect(() => {
    setCurrentImageIndex(0);
    setGalleryType('exterior');
    setSelectedUnitType(null);
    setActiveFloorTab(0);
    window.scrollTo(0, 0);
  }, [projectId]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <h2 className="text-2xl font-light text-white mb-4">
            {language === 'en' ? 'Project Not Found' : language === 'ar' ? 'المشروع غير موجود' : 'پڕۆژە نەدۆزرایەوە'}
          </h2>
          <Link to="/projects" className="text-gray-400 hover:text-white border-b border-white">
            {language === 'en' ? 'Back to Projects' : language === 'ar' ? 'العودة إلى المشاريع' : 'گەڕانەوە بۆ پڕۆژەکان'}
          </Link>
        </div>
      </div>
    );
  }

  const currentGallery = galleryType === 'exterior' ? project.exteriorImages : project.interiorImages;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/projects/inquiry`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        message: formData.message,
        project_id: projectId
      });
      toast.success(t('contact.form.success'));
      setFormData({ name: '', phone: '', email: '', message: '', unitType: '' });
    } catch (error) {
      console.error('Project inquiry error:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % currentGallery.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + currentGallery.length) % currentGallery.length);
  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden" data-testid="project-detail-page">
      {/* Decorative curved lines - Bugatti style */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden opacity-20">
        <svg className="absolute -left-1/4 top-0 h-full w-1/2" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0,0 Q50,50 0,100" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.2" />
          <path d="M10,0 Q60,50 10,100" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.2" />
          <path d="M20,0 Q70,50 20,100" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.2" />
        </svg>
        <svg className="absolute -right-1/4 top-0 h-full w-1/2" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M100,0 Q50,50 100,100" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.2" />
          <path d="M90,0 Q40,50 90,100" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.2" />
          <path d="M80,0 Q30,50 80,100" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.2" />
        </svg>
      </div>

      {/* Hero Section - Full Viewport */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" key={`hero-${project.id}`}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black z-10" />
        
        {/* Video or Image Background with Parallax */}
        {project.id === 'bakrajo_hills' ? (
          <video
            key={`video-bakrajo_hills`}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: `translateY(${scrollY * 0.3}px)` }}
          >
            <source src="https://ik.imagekit.io/9qjog4ow1/20587980-7bad-43d9-9bd0-f0c04ca40cad.mp4" type="video/mp4" />
          </video>
        ) : project.id === 'dina_city' ? (
          <video
            key={`video-dina_city`}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: `translateY(${scrollY * 0.3}px)` }}
          >
            <source src="https://ik.imagekit.io/vdrhxyigu/0210.mov" type="video/mp4" />
          </video>
        ) : project.id === 'dli_shar' ? (
          <video
            key={`video-dli_shar`}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: `translateY(${scrollY * 0.3}px)` }}
          >
            <source src="https://ik.imagekit.io/vdrhxyigu/IMG_1351.mov" type="video/mp4" />
          </video>
        ) : (
          <img
            key={`img-${project.id}`}
            src={project.exteriorImages[0]}
            alt={project.name[language] || project.name.en}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-100"
            style={{ transform: `translateY(${scrollY * 0.3}px) scale(1.1)` }}
          />
        )}
        
        {/* Hero Content */}
        <div className="relative z-20 text-center px-6 max-w-5xl mx-auto">
          {/* Location */}
          <p className="text-sm sm:text-base uppercase tracking-[0.3em] text-white/70 mb-8 font-light animate-fade-in-up animation-delay-100">
            {project.location}
          </p>
          
          {/* Project Logo - Main Title */}
          {project.logo && (
            <img
              src={project.logo}
              alt={project.name[language] || project.name.en}
              className="w-32 sm:w-40 md:w-48 h-auto mx-auto mb-10 animate-fade-in-up animation-delay-200"
            />
          )}
          
          {/* CTA Button */}
          <a
            href="#inquiry"
            className="inline-block px-10 py-4 border border-white/50 text-white text-xs uppercase tracking-[0.2em] font-light hover:bg-white hover:text-black transition-all duration-300 animate-fade-in-up animation-delay-400"
          >
            {t('cta.button')}
          </a>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <div className="w-px h-16 bg-gradient-to-b from-transparent to-white/50" />
        </div>
      </section>

      {/* Introduction Section */}
      <section className="relative py-24 sm:py-32 md:py-40 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <p className="text-lg sm:text-xl md:text-2xl text-white/80 font-light leading-relaxed">
            {project.description[language] || project.description.en}
          </p>
        </div>
      </section>

      {/* Introducing Section with Building Image */}
      <section className="relative py-24 sm:py-32 bg-black overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text Content */}
            <div className="order-2 lg:order-1">
              <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6 font-light">
                {language === 'en' ? 'INTRODUCING' : language === 'ar' ? 'نقدم لكم' : 'پێشکەشکردن'}
              </p>
              {/* Project Logo instead of text title */}
              {project.logo && (
                <img
                  src={project.logo}
                  alt={project.name[language] || project.name.en}
                  className="h-12 sm:h-14 md:h-16 w-auto mb-8"
                />
              )}
              <p className="text-base sm:text-lg text-white/70 font-light leading-relaxed mb-8">
                {project.description[language] || project.description.en}
              </p>
              <a
                href="#inquiry"
                className="inline-flex items-center gap-3 text-white text-xs uppercase tracking-[0.2em] font-light border-b border-white/50 pb-2 hover:border-white transition-colors"
              >
                {language === 'en' ? 'ENQUIRE NOW' : language === 'ar' ? 'استفسر الآن' : 'ئێستا بپرسە'}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            
            {/* Image */}
            <div className="order-1 lg:order-2 relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={project.exteriorImages[1] || project.exteriorImages[0]}
                  alt={project.name[language] || project.name.en}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Residences - Feature Cards Section - only show if not hidden */}
      {!project.hideLargeGallery && (
        <section className="relative py-24 sm:py-32 bg-black">
          <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
            {/* Section Header */}
            <div className="text-center mb-16 sm:mb-20">
              <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4 font-light">
                {language === 'en' ? 'THE RESIDENCES' : language === 'ar' ? 'المساكن' : 'نیشتەجێبوونەکان'}
              </p>
              <p className="text-lg sm:text-xl text-white/70 font-light max-w-3xl mx-auto leading-relaxed">
                {project.id === 'dli_shar' ? (
                  language === 'en' 
                    ? 'Rising majestically in the heart of the city, Dli Shar towers represent the pinnacle of urban luxury living with world-class amenities and breathtaking views.'
                    : language === 'ar'
                    ? 'ترتفع بشموخ في قلب المدينة، أبراج دلي شار تمثل قمة الحياة الفاخرة الحضرية مع مرافق عالمية المستوى وإطلالات خلابة.'
                    : 'بە شکۆمەندی لە دڵی شاردا هەڵدەستن، بورجەکانی دڵی شار لوتکەی ژیانی لوکسی شارۆچکەیی نوێنەرایەتی دەکەن بە ئیمکاناتی ئاستی جیهانی و دیمەنی سەرنجڕاکێش.'
                ) : project.id === 'bakrajo_hills' ? (
                  language === 'en'
                    ? 'Nestled among rolling hills, Bakrajo Hills offers an exclusive villa community where nature meets modern elegance, creating a serene retreat for discerning families.'
                    : language === 'ar'
                    ? 'تقع بين التلال المتموجة، باكراجو هيلز تقدم مجتمع فيلات حصري حيث تلتقي الطبيعة بالأناقة العصرية، مما يخلق ملاذًا هادئًا للعائلات المميزة.'
                    : 'لە نێوان گردەکان، بەکرەجۆ هیڵز کۆمەڵگەیەکی ڤیلای تایبەت پێشکەش دەکات کە سروشت لەگەڵ شیاوەکاری مۆدێرن دەگۆڕێت، پەنابەرێکی ئارام بۆ خێزانە هەڵبژێردراوەکان دروست دەکات.'
                ) : project.id === 'dina_city' ? (
                  language === 'en'
                    ? 'At the gateway of the city, Dina City presents a complete residential experience with modern apartments designed for comfort, convenience, and a promising future.'
                    : language === 'ar'
                    ? 'عند بوابة المدينة، تقدم دينا سيتي تجربة سكنية متكاملة مع شقق عصرية مصممة للراحة والملاءمة وتَعد بمستقبل واعد.'
                    : 'لە دەروازەی شار، دینا سیتی ئەزموونی نیشتەجێبوونی تەواو پێشکەش دەکات بە شووقەی مۆدێرن کە بۆ ئاسوودەیی و گونجاوی و داهاتوویەکی بەهیوا دیزاین کراوە.'
                ) : (
                  language === 'en' 
                    ? 'An architectural tour de force, born from a united vision of excellence. Unrivaled, unmatched, designed to etch a lasting legacy.'
                    : language === 'ar'
                    ? 'تحفة معمارية، ولدت من رؤية موحدة للتميز. لا مثيل لها، مصممة لتترك إرثًا دائمًا.'
                    : 'شاکارێکی تەواوی ئەندازیاری، لەدایک بووە لە بینینێکی یەکگرتوو بۆ باشی. هاوتا نییە، دیزاین کراوە بۆ جێهێشتنی میراتێکی بەردەوام.'
                )}
              </p>
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Card 1 - Masterpiece */}
              <div className="group text-center">
              <div className="aspect-[4/5] mb-8 overflow-hidden">
                <img
                  src={project.exteriorImages[2] || project.exteriorImages[0]}
                  alt="Masterpiece"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <h3 className="text-xl sm:text-2xl uppercase tracking-widest text-white mb-4 font-light">
                {language === 'en' ? 'MASTERPIECE' : language === 'ar' ? 'تحفة فنية' : 'شاکار'}
              </h3>
              <p className="text-white/60 font-light text-sm leading-relaxed mb-6">
                {project.id === 'dli_shar' ? (
                  language === 'en'
                    ? 'Towering high-rises with premium finishes, featuring panoramic city views and state-of-the-art facilities that redefine urban living standards.'
                    : language === 'ar'
                    ? 'أبراج شاهقة بتشطيبات فاخرة، تتميز بإطلالات بانورامية على المدينة ومرافق متطورة تعيد تعريف معايير الحياة الحضرية.'
                    : 'بورجی بەرز بە کەرەستەی پریمیۆم، بە دیمەنی پانۆرامی شار و ئیمکاناتی پێشکەوتوو کە ستانداردەکانی ژیانی شارۆچکەیی دووبارە پێناسە دەکات.'
                ) : project.id === 'bakrajo_hills' ? (
                  language === 'en'
                    ? 'Elegant villas with distinctive red-tiled roofs, blending Mediterranean charm with Kurdish heritage in a prestigious hillside community.'
                    : language === 'ar'
                    ? 'فيلات أنيقة بأسطح من القرميد الأحمر المميز، تمزج بين سحر البحر الأبيض المتوسط والتراث الكردي في مجتمع تلال مرموق.'
                    : 'ڤیلای شیاو بە سەربانی سووری تایبەت، جوانی مەدیتەرانی لەگەڵ میراتی کوردی تێکەڵ دەکات لە کۆمەڵگەیەکی گردی بەناوبانگدا.'
                ) : project.id === 'dina_city' ? (
                  language === 'en'
                    ? 'Modern apartments with smart layouts, combining functionality with aesthetic appeal at the gateway of Sulaymaniyah city.'
                    : language === 'ar'
                    ? 'شقق عصرية بتصاميم ذكية، تجمع بين الوظائف العملية والجاذبية الجمالية عند بوابة مدينة السليمانية'
                    : 'شووقەی مۆدێرن بە پلانی زیرەک، کارایی لەگەڵ جوانی تێکەڵ دەکات لە دەروازەی شاری سلێمانیدا.'
                ) : (
                  language === 'en'
                    ? 'An unrivaled masterful display of fascinating craftsmanship derived from a shared vision of excellence.'
                    : language === 'ar'
                    ? 'عرض بارع لا مثيل له للحرفية الرائعة المستمدة من رؤية مشتركة للتميز.'
                    : 'نمایشێکی شارەزایانەی هاوتا نییە لە پیشەسازی سەرنجڕاکێش کە لە بینینێکی هاوبەش بۆ باشی وەرگیراوە.'
                )}
              </p>
              <a href="#gallery" className="inline-flex items-center gap-2 text-white text-xs uppercase tracking-[0.15em] font-light border-b border-white/30 pb-1 hover:border-white transition-colors">
                {language === 'en' ? 'LEARN MORE' : language === 'ar' ? 'اعرف المزيد' : 'زیاتر بزانە'}
                <ArrowRight className="w-3 h-3" />
              </a>
            </div>

            {/* Card 2 - Distinction */}
            <div className="group text-center">
              <div className="aspect-[4/5] mb-8 overflow-hidden">
                <img
                  src={project.exteriorImages[3] || project.exteriorImages[0]}
                  alt="Distinction"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <h3 className="text-xl sm:text-2xl uppercase tracking-widest text-white mb-4 font-light">
                {language === 'en' ? 'DISTINCTION' : language === 'ar' ? 'تميز' : 'جیاوازی'}
              </h3>
              <p className="text-white/60 font-light text-sm leading-relaxed mb-6">
                {project.id === 'dli_shar' ? (
                  language === 'en'
                    ? 'Premium amenities including fitness centers, swimming pools, and landscaped gardens, all within reach of the city\'s vibrant commercial hub.'
                    : language === 'ar'
                    ? 'مرافق فاخرة تشمل مراكز لياقة بدنية ومسابح وحدائق منسقة، كلها في متناول اليد من المركز التجاري النابض بالحياة.'
                    : 'ئیمکاناتی پریمیۆم لەوانە ناوەندی وەرزشی و مەلەوانگە و باخچەی ڕێکخراو، هەموویان لە ناوەندی بازرگانی جموجوڵدار شاردا.'
                ) : project.id === 'bakrajo_hills' ? (
                  language === 'en'
                    ? 'Private gardens, spacious driveways, and family-friendly neighborhoods surrounded by natural beauty and fresh mountain air.'
                    : language === 'ar'
                    ? 'حدائق خاصة وممرات واسعة وأحياء صديقة للعائلات محاطة بالجمال الطبيعي وهواء الجبال المنعش.'
                    : 'باخچەی تایبەت و ڕێگای فراوان و گەڕەکی گونجاو بۆ خێزان بە دەوردراوی جوانی سروشتی و هەوای پاکی شاخ.'
                ) : project.id === 'dina_city' ? (
                  language === 'en'
                    ? 'Comprehensive facilities including schools, daycare, supermarket, gym, and swimming pool, creating a self-sufficient community.'
                    : language === 'ar'
                    ? 'مرافق شاملة تضم مدارس، ودور حضانة، وسوبرماركت، بالاضافة الى صالة رياضية ومسبح، مما يخلق مجتمعًا مكتفيًا ذاتيًا ينبض بالحياة'
                    : 'ئیمکاناتی تەواو لەوانە قوتابخانە و باخچەی منداڵان و سوپەرمارکێت و یاریگە و مەلەوانگە، کۆمەڵگەیەکی سەربەخۆ دروست دەکات.'
                ) : (
                  language === 'en'
                    ? 'From every angle, the façade offers a new perspective that creates visual intrigue and dynamic flow.'
                    : language === 'ar'
                    ? 'من كل زاوية، تمنح الواجهة منظورًا جديدًا يخلق شغفاً بصريًا وتدفقًا ديناميكيًا متجدداً.'
                    : 'لە هەموو لایەنێکەوە، دەرەوە ڕوانگەیەکی نوێ پێشکەش دەکات کە سەرنجی بینراو و جوڵەی داینامیک دروست دەکات.'
                )}
              </p>
              <a href="#gallery" className="inline-flex items-center gap-2 text-white text-xs uppercase tracking-[0.15em] font-light border-b border-white/30 pb-1 hover:border-white transition-colors">
                {language === 'en' ? 'LEARN MORE' : language === 'ar' ? 'اعرف المزيد' : 'زیاتر بزانە'}
                <ArrowRight className="w-3 h-3" />
              </a>
            </div>

            {/* Card 3 - Design */}
            <div className="group text-center">
              <div className="aspect-[4/5] mb-8 overflow-hidden">
                <img
                  src={project.interiorImages?.[0] || project.exteriorImages[0]}
                  alt="Design"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <h3 className="text-xl sm:text-2xl uppercase tracking-widest text-white mb-4 font-light">
                {language === 'en' ? 'DESIGN' : language === 'ar' ? 'تصميم' : 'دیزاین'}
              </h3>
              <p className="text-white/60 font-light text-sm leading-relaxed mb-6">
                {project.id === 'dli_shar' ? (
                  language === 'en'
                    ? 'Contemporary interiors with high ceilings, premium materials, and thoughtful layouts that maximize space and natural light.'
                    : language === 'ar'
                    ? 'تصميمات داخلية معاصرة بأسقف عالية ومواد فاخرة وتخطيطات مدروسة تعظم المساحة والضوء الطبيعي.'
                    : 'ناوەوەی سەردەمیانە بە سەقفی بەرز و کەرەستەی پریمیۆم و پلانی ورد کە شوێن و ڕووناکی سروشتی زیاد دەکات.'
                ) : project.id === 'bakrajo_hills' ? (
                  language === 'en'
                    ? 'Spacious villa interiors with multiple floor plans, featuring large bedrooms, modern kitchens, and seamless indoor-outdoor living.'
                    : language === 'ar'
                    ? 'تصميمات داخلية واسعة للفيلات بمخططات طوابق متعددة، تضم غرف نوم كبيرة ومطابخ عصرية وتواصل سلس بين الداخل والخارج.'
                    : 'ناوەوەی ڤیلای فراوان بە پلانی چەند نهۆم، ژووری نووستنی گەورە و چێشتخانەی مۆدێرن و پەیوەندی ئاسان لە نێوان ناوەوە و دەرەوەدا.'
                ) : project.id === 'dina_city' ? (
                  language === 'en'
                    ? 'Well-planned apartments with efficient use of space, modern finishes, and balconies that bring the city\'s energy into your home.'
                    : language === 'ar'
                    ? 'شقق مخططة بعناية مع استخدام فعال للمساحة، وتشطيبات عصرية، وشرفات تجلب طاقة المدينة إلى قلب منزلك'
                    : 'شووقەی بە پلان بە بەکارهێنانی کاریگەر بۆ شوێن و کەرەستەی مۆدێرن و بەلکۆن کە وزەی شار دەهێنێتە ماڵەکەت.'
                ) : (
                  language === 'en'
                    ? 'The design amalgamates modern and natural elements. Every inch of the space exudes quality and refinement.'
                    : language === 'ar'
                    ? 'يمتزج التصميم بين العناصر الحديثة والطبيعية. ليفيض كل شبر من المساحة بالجودة والرقي.'
                    : 'دیزاینەکە توخمە مۆدێرن و سروشتییەکان تێکەڵ دەکات. هەموو ئینچێک لە شوێنەکە کوالیتی و جوانی دەردەخات.'
                )}
              </p>
              <a href="#gallery" className="inline-flex items-center gap-2 text-white text-xs uppercase tracking-[0.15em] font-light border-b border-white/30 pb-1 hover:border-white transition-colors">
                {language === 'en' ? 'LEARN MORE' : language === 'ar' ? 'اعرف المزيد' : 'زیاتر بزانە'}
                <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Lifestyle Gallery Carousel */}
      <section className="relative py-24 sm:py-32 bg-black" id="gallery">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4 font-light">
              {language === 'en' ? 'LIFESTYLE' : language === 'ar' ? 'نمط الحياة' : 'شێوازی ژیان'}
            </p>
            <p className="text-lg text-white/70 font-light max-w-2xl mx-auto">
              {language === 'en'
                ? 'Your private retreat in the heart of the city, cultivating the art of living.'
                : language === 'ar'
                ? 'ملاذك الخاص في قلب المدينة، حيث نزرع فن العيش.'
                : 'پەناگەی تایبەتیت لە ناوەندی شاردا، پەروەردەی هونەری ژیان.'}
            </p>
          </div>

          {/* Gallery Type Toggle - only show if interior images exist */}
          {!project.hideInteriorGallery && project.interiorImages && project.interiorImages.length > 0 && (
            <div className="flex justify-center gap-4 mb-8">
              <button
                onClick={() => { setGalleryType('exterior'); setCurrentImageIndex(0); }}
                className={`px-8 py-3 text-xs uppercase tracking-[0.2em] font-light border transition-all duration-300 ${
                  galleryType === 'exterior'
                    ? 'bg-white text-black border-white'
                    : 'bg-transparent text-white/70 border-white/30 hover:border-white'
                }`}
              >
                {t('projectDetail.exterior')}
              </button>
              <button
                onClick={() => { setGalleryType('interior'); setCurrentImageIndex(0); }}
                className={`px-8 py-3 text-xs uppercase tracking-[0.2em] font-light border transition-all duration-300 ${
                  galleryType === 'interior'
                    ? 'bg-white text-black border-white'
                    : 'bg-transparent text-white/70 border-white/30 hover:border-white'
                }`}
              >
                {t('projectDetail.interior')}
              </button>
            </div>
          )}

          {/* Main Image Display */}
          <div className="relative aspect-video mb-6 group cursor-pointer overflow-hidden" onClick={() => openLightbox(currentImageIndex)}>
            <img
              src={currentGallery[currentImageIndex]}
              alt="Gallery"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <Maximize2 className="w-12 h-12 text-white" />
            </div>
            
            {/* Navigation Arrows */}
            {currentGallery.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 border border-white/30 bg-black/30 backdrop-blur-sm flex items-center justify-center hover:bg-white/10 transition-all"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 border border-white/30 bg-black/30 backdrop-blur-sm flex items-center justify-center hover:bg-white/10 transition-all"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </>
            )}

            {/* Image Counter */}
            <div className="absolute bottom-6 right-6 text-white text-xs uppercase tracking-widest bg-black/50 px-4 py-2">
              {currentImageIndex + 1} / {currentGallery.length}
            </div>
          </div>

          {/* Thumbnail Strip */}
          <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide">
            {currentGallery.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-24 h-16 sm:w-32 sm:h-20 overflow-hidden transition-all duration-300 ${
                  currentImageIndex === index ? 'ring-2 ring-white opacity-100' : 'opacity-50 hover:opacity-100'
                }`}
              >
                <img src={image} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Unit Types / Floor Plans Section */}
      {project.unitTypes && project.unitTypes.length > 0 && (
        <section className="relative py-24 sm:py-32 bg-[#0a0a0a]">
          <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
            {/* Check if unitTypes have categories (e.g., Dina City with villas/apartments) */}
            {project.unitTypes.some(ut => ut.category) ? (
              <>
                {/* Group by category */}
                {['villa', 'apartment'].filter(cat => project.unitTypes.some(ut => ut.category === cat)).map((cat) => (
                  <div key={cat} className={cat === 'apartment' ? 'mt-24' : ''}>
                    <div className="text-center mb-16">
                      <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4 font-light">
                        {cat === 'villa'
                          ? (language === 'en' ? 'VILLAS FLOOR PLANS' : language === 'ar' ? 'مخططات طوابق الفلل' : 'نەخشەی نهۆمەکانی ڤیلا')
                          : (language === 'en' ? 'APARTMENT FLOOR PLANS' : language === 'ar' ? 'مخططات طوابق الشقق' : 'نەخشەی نهۆمەکانی شوقە')}
                      </p>
                      <p className="text-lg text-white/70 font-light max-w-2xl mx-auto">
                        {language === 'en'
                          ? 'Meticulously curated layouts offering captivating views and open-plan living spaces.'
                          : language === 'ar'
                          ? 'تصميمات منسقة بعناية تمنحك إطلالات آسرة ومساحات معيشة مفتوحة.'
                          : 'دیزاینی بە وریایی ئامادەکراو کە دیمەنی سەرنجڕاکێش و شوێنی ژیانی کراوە پێشکەش دەکات.'}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      {project.unitTypes.filter(ut => ut.category === cat).map((unitType) => (
                        <button
                          key={unitType.id}
                          onClick={() => { setSelectedUnitType(unitType); setActiveFloorTab(0); }}
                          className="group text-left bg-black/50 border border-white/10 p-6 hover:border-white/30 transition-all duration-300"
                        >
                          <div className="aspect-square mb-6 overflow-hidden bg-white/5">
                            <img
                              src={unitType.floorPlans ? unitType.floorPlans[0]?.image : unitType.floorPlan}
                              alt={unitType.name}
                              className="w-full h-full object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                            />
                          </div>
                          <h3 className="text-lg uppercase tracking-widest text-white mb-2 font-light">
                            {unitType.name}
                          </h3>
                          <p className="text-white/50 text-sm font-light mb-4">{unitType.area}</p>
                          <div className="flex items-center gap-2 text-white/50 text-sm font-light">
                            <Home className="w-4 h-4" />
                            <span>{unitType.bedrooms} {t('projectDetail.bedrooms')}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
                <div className="text-center mb-16">
                  <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4 font-light">
                    {language === 'en' ? 'FLOOR PLANS' : language === 'ar' ? 'مخططات الطوابق' : 'نەخشەی نهۆمەکان'}
                  </p>
                  <p className="text-lg text-white/70 font-light max-w-2xl mx-auto">
                    {language === 'en'
                      ? 'Meticulously curated layouts offering captivating views and open-plan living spaces.'
                      : language === 'ar'
                      ? 'تصميمات منسقة بعناية تمنحك إطلالات آسرة ومساحات معيشة مفتوحة.'
                      : 'دیزاینی بە وریایی ئامادەکراو کە دیمەنی سەرنجڕاکێش و شوێنی ژیانی کراوە پێشکەش دەکات.'}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {project.unitTypes.map((unitType) => (
                    <button
                      key={unitType.id}
                      onClick={() => { setSelectedUnitType(unitType); setActiveFloorTab(0); }}
                      className="group text-left bg-black/50 border border-white/10 p-6 hover:border-white/30 transition-all duration-300"
                    >
                      <div className="aspect-square mb-6 overflow-hidden bg-white/5">
                        <img
                          src={unitType.floorPlans ? unitType.floorPlans[0]?.image : unitType.floorPlan}
                          alt={unitType.name}
                          className="w-full h-full object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                        />
                      </div>
                      <h3 className="text-lg uppercase tracking-widest text-white mb-2 font-light">
                        {unitType.name}
                      </h3>
                      <p className="text-white/50 text-sm font-light mb-4">{unitType.area}</p>
                      <div className="flex items-center gap-2 text-white/50 text-sm font-light">
                        <Home className="w-4 h-4" />
                        <span>{unitType.bedrooms} {t('projectDetail.bedrooms')}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Amenities Section */}
      <section className="relative py-24 sm:py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4 font-light">
              {t('projectDetail.amenities')}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {project.amenities.map((amenity, index) => (
              <div
                key={index}
                className="text-center py-6 px-4 border border-white/10 hover:border-white/30 transition-colors"
              >
                <span className="text-white/70 font-light text-sm">{amenity}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form Section */}
      <section id="inquiry" className="relative py-24 sm:py-32 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4 font-light">
              {language === 'en' ? 'REGISTER YOUR INTEREST' : language === 'ar' ? 'سجل اهتمامك' : 'خولیاکەت تۆمار بکە'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {t('projectDetail.interested')}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <input
                type="text"
                placeholder={t('contact.form.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-6 py-4 bg-transparent border border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none transition-colors font-light"
                required
              />
              <input
                type="tel"
                placeholder={t('contact.form.phone')}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-6 py-4 bg-transparent border border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none transition-colors font-light"
                required
              />
            </div>
            <input
              type="email"
              placeholder={t('contact.form.email')}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-6 py-4 bg-transparent border border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none transition-colors font-light"
              required
            />
            {project.unitTypes && project.unitTypes.length > 0 && (
              <select
                value={formData.unitType}
                onChange={(e) => setFormData({ ...formData, unitType: e.target.value })}
                className="w-full px-6 py-4 bg-black border border-white/20 text-white focus:border-white focus:outline-none transition-colors font-light"
              >
                <option value="" className="bg-black">{t('projectDetail.selectUnitType')}</option>
                {project.unitTypes.map((unit) => (
                  <option key={unit.id} value={unit.id} className="bg-black">
                    {unit.name} - {unit.area}
                  </option>
                ))}
              </select>
            )}
            <textarea
              placeholder={t('contact.form.message')}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={4}
              className="w-full px-6 py-4 bg-transparent border border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none transition-colors resize-none font-light"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-5 bg-white text-black text-xs uppercase tracking-[0.2em] font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
            >
              {isSubmitting 
                ? (language === 'en' ? 'Sending...' : language === 'ar' ? 'جاري الإرسال...' : 'ناردن...') 
                : t('contact.form.submit')}
            </button>
          </form>
        </div>
      </section>

      {/* Download Brochure Banner */}
      {project.brochure && (
      <section className="relative py-16 bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl font-bold text-black tracking-tight">
              {t('projectDetail.downloadBrochure')}
            </h3>
            <p className="text-black/60 font-light">
              {language === 'en' ? 'Get the complete project details' : language === 'ar' ? 'احصل على تفاصيل المشروع الكاملة' : 'وردەکاری تەواوی پڕۆژەکە وەربگرە'}
            </p>
          </div>
          <a 
            href={project.brochure} 
            target="_blank" 
            rel="noopener noreferrer"
            download
            className="flex items-center gap-3 px-10 py-4 bg-black text-white text-xs uppercase tracking-[0.2em] font-light hover:bg-black/90 transition-colors"
          >
            <Download className="w-5 h-5" />
            {language === 'en' ? 'DOWNLOAD' : language === 'ar' ? 'تحميل' : 'داگرتن'}
          </a>
        </div>
      </section>
      )}

      {/* Unit Type Modal */}
      {selectedUnitType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/95">
          <div className="bg-[#111] max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-white/10">
            <div className="sticky top-0 bg-[#111] border-b border-white/10 p-8 flex justify-between items-start">
              <div>
                <h2 className="text-3xl font-bold text-white tracking-wide mb-2">
                  {selectedUnitType.name}
                </h2>
                <div className="text-sm text-white/60 font-light">
                  {selectedUnitType.area} • {selectedUnitType.bedrooms} Bedrooms
                  {selectedUnitType.floors && ` • ${selectedUnitType.floors} Floors`}
                </div>
              </div>
              <button
                onClick={() => { setSelectedUnitType(null); setActiveFloorTab(0); }}
                className="text-white/40 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-8">
              {/* Floor Plan Tabs - only show if floorPlans array exists */}
              {selectedUnitType.floorPlans && selectedUnitType.floorPlans.length > 1 && (
                <div className="flex gap-2 mb-6">
                  {selectedUnitType.floorPlans.map((floor, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveFloorTab(index)}
                      className={`px-6 py-3 text-xs uppercase tracking-[0.15em] font-light transition-all border ${
                        activeFloorTab === index
                          ? 'bg-white text-black border-white'
                          : 'bg-transparent text-white/60 border-white/20 hover:border-white/40'
                      }`}
                    >
                      {floor.name}
                    </button>
                  ))}
                </div>
              )}
              
              <div className="mb-8 bg-white/5 p-4">
                {selectedUnitType.floorPlans ? (
                  <img
                    src={selectedUnitType.floorPlans[activeFloorTab]?.image}
                    alt={`${selectedUnitType.name} - ${selectedUnitType.floorPlans[activeFloorTab]?.name}`}
                    className="w-full h-auto"
                  />
                ) : (
                  <img
                    src={selectedUnitType.floorPlan}
                    alt={selectedUnitType.name}
                    className="w-full h-auto"
                  />
                )}
              </div>
              
              <div>
                <h3 className="text-xl font-light text-white mb-6 tracking-wide uppercase">Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedUnitType.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 py-3 border-b border-white/10">
                      <div className="w-1 h-full bg-white/50" />
                      <span className="text-white/70 font-light text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {isLightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          <button
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-6 right-6 w-14 h-14 border border-white/30 flex items-center justify-center hover:bg-white/10 transition-all"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={currentGallery[currentImageIndex]}
            alt="Lightbox"
            className="max-w-full max-h-full object-contain"
          />
          {currentGallery.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 border border-white/30 flex items-center justify-center hover:bg-white/10 transition-all"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 border border-white/30 flex items-center justify-center hover:bg-white/10 transition-all"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </>
          )}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-xs uppercase tracking-widest">
            {currentImageIndex + 1} / {currentGallery.length}
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx="true">{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 1s ease-out forwards;
        }
        .animation-delay-100 {
          animation-delay: 0.1s;
          opacity: 0;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
        }
        .animation-delay-300 {
          animation-delay: 0.3s;
          opacity: 0;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
          opacity: 0;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default ProjectDetailPage;
